import React from 'react';

import css from './SellerRating.module.css';

const STARS = [1, 2, 3, 4, 5];

/**
 * Self-contained star icon (no host-app imports, same rule as Badge).
 * Fill color comes from SellerRating.module.css via data-filled.
 */
const Star = ({ isFilled }) => (
  <svg
    className={css.star}
    data-filled={isFilled}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

/**
 * Average seller rating: five stars (filled up to the rounded average) with an
 * optional link below (e.g. "See Reviews" pointing to the seller's profile).
 *
 * Data source: the empavision seller-rating enrichment. An empavision job
 * aggregates the seller's received reviews via the Integration API and writes
 * `publicData.sellerRating = { average, count }` back to the seller's user
 * profile (same write-back pattern as the price-drop enrichment). Because
 * listing and search queries already `include: ['author']`, the host app has
 * the data everywhere without any extra fetching — just pass the user entity.
 *
 * Props:
 * - user:           Sharetribe user entity (listing author); rating is read
 *                   from user.attributes.profile.publicData.sellerRating
 * - rating / reviewCount: optional explicit override for hosts that compute
 *                   the stats themselves; takes precedence over `user`
 * - seeReviewsLink: node, host-provided link element rendered under the stars
 * - showLabel:      bool, also render the average and count as visible text
 *                   ("4.0 (2)"). Off by default — the stars alone can't say how
 *                   many reviews back them, so hosts that care about that pass it.
 * - emptyLabel:     node rendered INSTEAD of the stars when the seller has no
 *                   reviews yet (e.g. "No reviews yet"). Off by default, which
 *                   keeps the original render-nothing behaviour.
 * - className:      string, optional host override for the root element
 *
 * Renders nothing when the seller has no (known) reviews yet and no emptyLabel
 * was given.
 */
export const SellerRating = ({
  user,
  rating,
  reviewCount,
  seeReviewsLink,
  showLabel,
  emptyLabel,
  className,
}) => {
  const sellerRating = user?.attributes?.profile?.publicData?.sellerRating || {};
  const average = typeof rating === 'number' ? rating : sellerRating.average;
  const count = typeof reviewCount === 'number' ? reviewCount : sellerRating.count;
  const rootClassName = className ? `${css.root} ${className}` : css.root;

  if (!count || !(average > 0)) {
    // Nothing to rate yet. The link is deliberately dropped in this state — it
    // would promise a review list that turns out to be empty.
    return emptyLabel ? (
      <div className={rootClassName}>
        <span className={css.empty}>{emptyLabel}</span>
      </div>
    ) : null;
  }

  const rounded = Math.round(average);
  const label = `${average.toFixed(1)}/5 (${count})`;

  return (
    <div className={rootClassName}>
      <span className={css.starsRow}>
        <span className={css.stars} title={label} role="img" aria-label={label}>
          {STARS.map(star => (
            <Star key={star} isFilled={star <= rounded} />
          ))}
        </span>
        {showLabel ? (
          // aria-hidden: the stars span above already announces the same figures.
          <span className={css.label} aria-hidden="true">
            {`${average.toFixed(1)} (${count})`}
          </span>
        ) : null}
      </span>
      {seeReviewsLink ? <span className={css.link}>{seeReviewsLink}</span> : null}
    </div>
  );
};
